import { useRef, useState, type FormEvent } from 'react';
import type { RosterImportRow } from '@chemoflow/contracts';
import { Icon } from '../components/Icon.js';
import { useAudit, useRosterImport, useRosterRefresh, useSettingsMutation } from '../api/hooks.js';
import { decodeCsv, parseCSV, toRosterRows } from '../lib/csv.js';
import { download, toCSV } from '../lib/export.js';
import { fmt } from '../lib/format.js';
import { useBoardData } from '../state/board.js';
import { useUi } from '../state/ui.js';

const SAMPLE_CSV: (string | number)[][] = [
  ['患者ID', '氏名', '診療科', '年齢', '性別', '生年月日', 'Rp数'],
  ['0001234567', '渡辺 隆', '消化器内科', 70, '男性', '1956/03/14', 2],
  ['0002345678', '山田 花子', '消化器内科', 68, '女性', '1958/05/02', 1],
  ['0004567890', '中村 美穂', '乳腺外科', 49, '女性', '1977/08/11', 3],
  ['0006789012', '加藤 由美', '婦人科', 55, '女性', '1971/02/27', 2],
  ['0003456789', '佐藤 健一', '消化器外科', 58, '男性', '1968/06/30', 2],
];

/** 名簿・設定・操作ログ。 */
export function AdminView() {
  const { cases, settings } = useBoardData();
  const { ask, toast } = useUi();
  const audit = useAudit(true);
  const importRoster = useRosterImport();
  const refreshRoster = useRosterRefresh();
  const saveSettings = useSettingsMutation();
  const fileRef = useRef<HTMLInputElement>(null);
  const [alertMin, setAlertMin] = useState(settings.alertMin);

  const onCsv = async (file: File) => {
    const rows = toRosterRows(parseCSV(decodeCsv(await file.arrayBuffer())));
    if (!rows.length) {
      toast('取り込める行がありませんでした。列の並びを確認してください');
      return;
    }
    const ok = await ask({
      title: `${rows.length}人の名簿を取り込みます`,
      text: '今日の名簿を置き換えます。\nすでに入力された状態はすべて消えます。',
      ok: '取り込む',
    });
    if (!ok) return;
    try {
      await importRoster.mutateAsync({ mode: 'replace', rows });
      toast(`${rows.length}人の名簿を取り込みました`);
    } catch {
      toast('名簿を取り込めませんでした');
    }
  };

  const onAdd = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const row: RosterImportRow = {
      patientId: String(form.get('id') ?? '').trim(),
      name: String(form.get('name') ?? '').trim(),
      dept: String(form.get('dept') ?? '').trim(),
      age: Number(form.get('age')) || null,
      sex: String(form.get('sex') ?? ''),
      birth: String(form.get('birth') ?? '').trim(),
      rpCount: Number(form.get('rp')) || 1,
    };
    if (cases.some((c) => c.patient?.patientId === row.patientId)) {
      toast(`患者ID ${row.patientId} はすでに名簿にあります`);
      return;
    }
    try {
      await importRoster.mutateAsync({ mode: 'append', rows: [row] });
      e.currentTarget.reset();
      toast(`${row.name}（${row.patientId}）を名簿に追加しました`);
    } catch {
      toast('名簿に追加できませんでした');
    }
  };

  const onRefresh = async () => {
    try {
      const result = await refreshRoster.mutateAsync();
      toast(`名簿を再取得しました（追加 ${result.added}人）`);
    } catch {
      toast('院内の対象患者リストを取得できませんでした');
    }
  };

  const saveAlertMin = async (value: number) => {
    const next = Math.min(180, Math.max(1, value || 1));
    setAlertMin(next);
    await saveSettings.mutateAsync({ alertMin: next });
    toast('設定を保存しました');
  };

  const saveExtension = async (index: number, no: string) => {
    const ext = settings.ext.map((x, i) => (i === index ? { ...x, no: no.trim() } : x));
    await saveSettings.mutateAsync({ ext });
    toast('内線番号を保存しました');
  };

  return (
    <main className="board is-one view" id="view-admin" data-view="admin">
      <section>
        <div className="panel">
          <h2>
            患者名簿 <small>本日 {cases.length}人</small>
          </h2>
          <p className="fine">
            前日の締切（14時）までに申し込みのあった外来ケモ患者を CSV で取り込みます。
            <br />
            列：患者ID, 氏名, 診療科, 年齢, 性別, 生年月日, Rp数（1行目は見出し。UTF-8 / Shift_JIS
            どちらでも可）
          </p>
          <div className="btns" style={{ marginTop: 16 }}>
            <label className="pill-btn">
              <Icon name="upload_file" />
              CSVを取り込む
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (file) void onCsv(file);
                }}
              />
            </label>
            <button className="pill-btn ghost" type="button" onClick={() => void onRefresh()}>
              <Icon name="autorenew" />
              院内から再取得
            </button>
            <button
              className="pill-btn ghost"
              type="button"
              onClick={() =>
                download('ケモフロー_名簿_書式見本.csv', toCSV(SAMPLE_CSV), 'text/csv')
              }
            >
              <Icon name="download" />
              書式見本をダウンロード
            </button>
          </div>
        </div>

        <div className="panel">
          <h2>
            時間外の患者を追加 <small>締切後〜翌朝に申し込まれた患者</small>
          </h2>
          <form className="form" autoComplete="off" onSubmit={(e) => void onAdd(e)}>
            <label className="field">
              <span>患者ID</span>
              <input name="id" required inputMode="numeric" placeholder="0001234567" />
            </label>
            <label className="field">
              <span>氏名</span>
              <input name="name" required placeholder="山田 花子" />
            </label>
            <label className="field">
              <span>診療科</span>
              <input name="dept" required placeholder="消化器内科" />
            </label>
            <label className="field">
              <span>年齢</span>
              <input name="age" type="number" min={0} max={120} placeholder="65" />
            </label>
            <label className="field">
              <span>性別</span>
              <select name="sex" defaultValue="女性">
                <option>女性</option>
                <option>男性</option>
              </select>
            </label>
            <label className="field">
              <span>生年月日</span>
              <input name="birth" placeholder="1960/04/01" />
            </label>
            <label className="field">
              <span>Rp数</span>
              <input name="rp" type="number" min={1} max={9} defaultValue={1} />
            </label>
            <div className="field" style={{ justifyContent: 'flex-end' }}>
              <button className="pill-btn" type="submit">
                <Icon name="add" />
                追加する
              </button>
            </div>
          </form>
        </div>

        <div className="panel">
          <h2>設定</h2>
          <div className="form">
            <label className="field unit" data-unit="分">
              <span>滞留アラート（確定から）</span>
              <input
                type="number"
                min={5}
                max={180}
                value={alertMin}
                onChange={(e) => setAlertMin(Number(e.target.value))}
                onBlur={(e) => void saveAlertMin(Number(e.target.value))}
              />
            </label>
          </div>
          <h2 style={{ marginTop: 24 }}>
            内線番号 <small>縮退時・調製開始後の変更／中止で表示</small>
          </h2>
          <div className="form">
            {settings.ext.map((x, i) => (
              <label className="field" key={x.label}>
                <span>{x.label}</span>
                <input
                  defaultValue={x.no}
                  inputMode="numeric"
                  onBlur={(e) => void saveExtension(i, e.target.value)}
                />
              </label>
            ))}
          </div>
        </div>

        <div className="panel">
          <h2>
            操作ログ <small>誰が・いつ・どの患者に・何をしたか</small>
          </h2>
          <p className="fine">
            ログに残るのは操作者と代理キーまでで、患者氏名は保存されません。
            氏名は表示のたびに院内から取得しています。
          </p>
          <ul className="log">
            {audit.data?.entries.length ? (
              audit.data.entries.slice(0, 60).map((l, i) => {
                const p = l.surrogateKey ? audit.data.patients[l.surrogateKey] : undefined;
                return (
                  <li key={`${l.at}-${i}`}>
                    <time>{fmt(l.at)}</time>
                    <span className="by">
                      {l.actorName}（{l.actorRole}）
                    </span>
                    <span className="act">{l.action}</span>
                    <span className="pt">
                      {p ? `${p.name}　${p.patientId}` : (l.surrogateKey ?? '')}
                    </span>
                  </li>
                );
              })
            ) : (
              <li>
                <span className="by">まだ操作はありません</span>
              </li>
            )}
          </ul>
        </div>
      </section>
    </main>
  );
}
